import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { db } from '../utils/firebaseConfig';
import { doc, getDoc } from 'firebase/firestore';

const OnlineUsersTooltip = ({ onlineUsers }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [userDetails, setUserDetails] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ left: 0, top: 0, showLeft: false });
  const tooltipRef = useRef(null);

  // Detect if device is mobile
  useEffect(() => {
    const checkIsMobile = () => {
      setIsMobile('ontouchstart' in window || navigator.maxTouchPoints > 0);
    };
    
    checkIsMobile();
    window.addEventListener('resize', checkIsMobile);
    
    return () => window.removeEventListener('resize', checkIsMobile);
  }, []);

  // Calculate tooltip position - memoized with useCallback
  const calculateTooltipPosition = useCallback(() => {
    if (!tooltipRef.current) return;

    const rect = tooltipRef.current.getBoundingClientRect();
    const windowWidth = window.innerWidth;
    const tooltipWidth = 200; // min-w-[200px]
    const padding = 16; // 1rem
    
    // Check if tooltip would overflow on the right
    const wouldOverflowRight = rect.right + tooltipWidth + padding > windowWidth;
    
    // On mobile or when it would overflow, position to the left
    const showLeft = isMobile || wouldOverflowRight;
    
    let left, top;
    
    if (showLeft) {
      // Position to the left of the trigger element
      left = rect.left - tooltipWidth - 8;
      // Ensure it doesn't go off the left edge
      if (left < padding) {
        left = padding;
      }
    } else {
      // Position to the right of the trigger element (default)
      left = rect.left + 16;
    }
    
    top = rect.bottom + 8;
    
    // Ensure tooltip doesn't go off the bottom of the screen
    const maxTop = window.innerHeight - 300; // approximate tooltip height
    if (top > maxTop) {
      top = rect.top - 8; // Position above instead
    }

    setTooltipPosition({ left, top, showLeft });
  }, [isMobile]);

  // Update position when tooltip becomes visible
  useEffect(() => {
    if (isVisible) {
      calculateTooltipPosition();
    }
  }, [isVisible, calculateTooltipPosition]);

  // Close tooltip when clicking outside on mobile
  useEffect(() => {
    if (!isMobile || !isVisible) return;

    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setIsVisible(false);
      }
    };

    document.addEventListener('touchstart', handleClickOutside);
    document.addEventListener('click', handleClickOutside);
    
    return () => {
      document.removeEventListener('touchstart', handleClickOutside);
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMobile, isVisible]);

  // Fetch user details whenever onlineUsers changes
  useEffect(() => {
    
    if (!onlineUsers.length) {
      setUserDetails([]);
      return;
    }
    
    const fetchUserDetails = async () => {
      setIsLoading(true);
      
      try {
        const detailsPromises = onlineUsers.map(async (userId) => {
          try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            
            if (!userDoc.exists()) {
              return {
                id: userId,
                name: 'Unknown User',
                role: 'User'
              };
            }
            
            const userData = userDoc.data();
            return {
              id: userId,
              name: userData.name || userData.displayName || 'Unknown User',
              role: userData.role || 'User'
            };
          } catch (error) {
            console.error(`OnlineUsersTooltip: Error fetching user ${userId}:`, error);
            return {
              id: userId,
              name: 'Unknown User',
              role: 'User'
            };
          }
        });

        const newDetails = await Promise.all(detailsPromises);
        setUserDetails(newDetails);
      } catch (error) {
        console.error('OnlineUsersTooltip: Error fetching user details:', error);
        setUserDetails([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserDetails();
  }, [onlineUsers]);

  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsVisible(true);
    }
  };
  
  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsVisible(false);
    }
  };
  
  const handleClick = (e) => {
    if (isMobile) {
      e.preventDefault();
      e.stopPropagation();
      setIsVisible(!isVisible);
    }
  };

  return (
    <div
      className="relative inline-block"
      ref={tooltipRef}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleClick}
    >
      <div className="flex items-center text-green-600 dark:text-green-400 cursor-pointer">
        <span className="w-1.5 h-1.5 rounded-full bg-green-500 dark:bg-green-400 animate-pulse mr-1"></span>
        <span className="text-sm">{onlineUsers.length} online</span>
      </div>

      {isVisible && createPortal(
        <div
          className="fixed min-w-[200px] max-w-[calc(100vw-2rem)] px-4 py-3 text-sm rounded-lg shadow-2xl border"
          style={{
            left: `${tooltipPosition.left}px`,
            top: `${tooltipPosition.top}px`,
            maxHeight: '60vh',
            overflowY: 'auto',
            backgroundColor: 'var(--color-primary)',
            color: 'var(--color-text)',
            borderColor: 'var(--color-secondary)',
            zIndex: 99999
          }}
        >
          {/* Arrow - positioned differently based on tooltip position */}
          <div 
            className="absolute w-3 h-3 transform rotate-45 -translate-x-1/2 -top-1.5"
            style={{
              left: tooltipPosition.showLeft ? 'calc(100% - 2rem)' : '2rem', // Arrow on right when tooltip is on left, arrow on left when tooltip is on right
              backgroundColor: 'var(--color-primary)',
              borderColor: 'var(--color-secondary)',
              borderTopWidth: '1px',
              borderLeftWidth: '1px'
            }}
          ></div>
          <div className="relative">
            <h4 className="font-medium mb-2" style={{ color: 'var(--color-text)' }}>Online Users</h4>
            {isLoading ? (
              <div className="flex justify-center py-2">
                <div className="animate-spin rounded-full h-5 w-5 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
              </div>
            ) : userDetails.length === 0 ? (
              <p className="py-2" style={{ color: 'var(--color-text)', opacity: 0.6 }}>No users online</p>
            ) : (
              <ul className="space-y-2">
                {userDetails.map((user) => (
                  <li key={user.id} style={{ color: 'var(--color-text)' }}>
                    {user.name}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default OnlineUsersTooltip;